import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate, Link } from "react-router-dom";
import {
  Flag, RefreshCw, Activity, Users, Map as MapIcon, EyeOff, Eye, CheckCircle2, XCircle,
  Loader2, ChevronDown, ChevronUp, AlertTriangle, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ISSUE_TYPES, SUB_CATEGORIES, labelForSubCategory } from "../components/ReportIssueModal";
import ExternalLink from "../components/ExternalLink";

const STATUS_TABS = [
  { value: "active", label: "Active" },
  { value: "resolved", label: "Resolved" },
  { value: "dismissed", label: "Dismissed" },
  { value: "all", label: "All" },
];

const STATUS_STYLE = {
  active: "bg-brand-accent/15 text-brand-accent border-brand-accent/30",
  resolved: "bg-brand-primary/15 text-brand-primary border-brand-primary/30",
  dismissed: "bg-brand-sand text-brand-subtle border-brand-border",
};

function labelForIssue(value) {
  return ISSUE_TYPES.find((t) => t.value === value)?.label || value;
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso; }
}

function absUrl(u) {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${BACKEND_URL}${u}`;
}

function ReportRow({ report, expanded, onToggle, onStatus, onHideTrail, onSaveEdit, busyId }) {
  const imgSrc = absUrl(report.image_url);
  const [editType, setEditType] = useState(report.issue_type);
  const [editSub, setEditSub] = useState(report.sub_category || "");
  const [notes, setNotes] = useState(report.admin_notes || "");
  const subOptions = SUB_CATEGORIES[editType] || [];

  // Reset edit form whenever the report identity changes
  React.useEffect(() => {
    setEditType(report.issue_type);
    setEditSub(report.sub_category || "");
    setNotes(report.admin_notes || "");
  }, [report.report_id, report.issue_type, report.sub_category, report.admin_notes]);

  return (
    <>
      <tr
        data-testid={`report-row-${report.report_id}`}
        onClick={onToggle}
        className="border-t border-brand-border/40 cursor-pointer hover:bg-brand-sand/40 transition-colors"
      >
        <td className="px-4 sm:px-5 py-3 w-20">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt=""
              className="w-14 h-14 object-cover rounded-lg border border-brand-border"
              data-testid={`report-thumb-${report.report_id}`}
            />
          ) : (
            <div className="w-14 h-14 rounded-lg border border-dashed border-brand-border flex items-center justify-center text-brand-subtle">
              <ImageIcon size={18} />
            </div>
          )}
        </td>
        <td className="px-3 py-3 text-brand-text">
          <div className="font-medium">{labelForIssue(report.issue_type)}</div>
          <div className="text-xs text-brand-subtle mt-0.5 truncate max-w-[260px]">
            {report.trail_name || report.trail_id} · {report.trail_region || "—"}
          </div>
        </td>
        <td className="px-3 py-3 hidden md:table-cell text-brand-subtle text-sm">
          <span className="line-clamp-2 max-w-[280px]">
            {report.description || <span className="italic">No description</span>}
          </span>
        </td>
        <td className="px-3 py-3 hidden sm:table-cell text-brand-subtle whitespace-nowrap text-xs">
          {fmtDate(report.created_at)}
        </td>
        <td className="px-3 py-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_STYLE[report.status] || ""}`}>
            {report.status}
          </span>
          {report.resolution_reason === "community_resolved" && (
            <span
              data-testid={`community-resolved-${report.report_id}`}
              className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-brand-primary/10 border-brand-primary/30 text-brand-primary uppercase tracking-wide"
              title="Auto-archived after 3 walkers marked this path clear"
            >
              Community
            </span>
          )}
          {report.status === "active" && (
            <div className="mt-1 flex gap-1.5 text-[10px] text-brand-subtle whitespace-nowrap" data-testid={`report-vote-counts-${report.report_id}`}>
              <span title="Still Here verifications">
                🛡 {(report.still_here_votes || []).length}
              </span>
              <span title="Path Clear verifications">
                ✅ {(report.path_clear_votes || []).length}
              </span>
            </div>
          )}
        </td>
        <td className="px-4 sm:px-5 py-3 text-right text-brand-subtle">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-brand-sand/60 border-t border-brand-border/40" data-testid={`report-expanded-${report.report_id}`}>
          <td colSpan={6} className="px-4 sm:px-6 py-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                {imgSrc ? (
                  <ExternalLink
                    href={imgSrc}
                    onClick={(e) => e.stopPropagation()}
                    testid={`report-fullimg-${report.report_id}`}
                  >
                    <img
                      src={imgSrc}
                      alt="Evidence"
                      className="w-full max-h-80 object-contain rounded-2xl border border-brand-border bg-brand-surface"
                    />
                  </ExternalLink>
                ) : (
                  <div className="w-full h-40 rounded-2xl border border-dashed border-brand-border flex items-center justify-center text-brand-subtle text-sm">
                    No photo submitted
                  </div>
                )}
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="td-section-label">Category</div>
                  <select
                    data-testid={`report-edit-type-${report.report_id}`}
                    value={editType}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { setEditType(e.target.value); setEditSub(""); }}
                    className="td-input mt-1 !text-sm"
                  >
                    {ISSUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  {subOptions.length > 0 && (
                    <select
                      data-testid={`report-edit-sub-${report.report_id}`}
                      value={editSub}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setEditSub(e.target.value)}
                      className="td-input mt-2 !text-sm"
                    >
                      <option value="">— No sub-category —</option>
                      {subOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <div className="td-section-label">Trail</div>
                  <div className="text-brand-text mt-1">
                    {report.trail_name || report.trail_id}
                    {report.trail_region && <span className="text-brand-subtle"> · {report.trail_region}</span>}
                  </div>
                </div>
                <div>
                  <div className="td-section-label">Description</div>
                  <div className="text-brand-text mt-1 whitespace-pre-wrap break-words">
                    {report.description || <span className="text-brand-subtle italic">No description provided.</span>}
                  </div>
                </div>
                <div>
                  <div className="td-section-label">Reporter</div>
                  <div className="text-brand-text mt-1 text-xs">
                    {report.reporter_email || (report.reporter_user_id ? `User ${report.reporter_user_id}` : "Anonymous walker")}
                  </div>
                </div>
                <div>
                  <div className="td-section-label">Admin notes</div>
                  <textarea
                    data-testid={`report-edit-notes-${report.report_id}`}
                    value={notes}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="Triage notes for the team…"
                    className="td-input mt-1 !text-sm resize-none"
                  />
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    data-testid={`report-save-edits-${report.report_id}`}
                    type="button"
                    disabled={busyId === report.report_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSaveEdit({
                        report_id: report.report_id,
                        issue_type: editType,
                        sub_category: editSub,
                        admin_notes: notes,
                      });
                    }}
                    className="td-btn-secondary !py-1.5 !px-3 !text-xs !text-brand-primary !border-brand-primary/40"
                  >
                    Save edits
                  </button>
                  <Link
                    to={`/map?focus=${report.trail_id}`}
                    data-testid={`report-view-trail-${report.report_id}`}
                    className="td-btn-secondary !py-1.5 !px-3 !text-xs"
                  >
                    <MapIcon size={13} /> View trail
                  </Link>
                  <button
                    data-testid={`report-close-trail-${report.report_id}`}
                    type="button"
                    disabled={busyId === report.report_id}
                    onClick={(e) => { e.stopPropagation(); onHideTrail(report); }}
                    className="td-btn-secondary !py-1.5 !px-3 !text-xs !text-brand-error !border-brand-error/30"
                  >
                    <EyeOff size={13} /> Close trail
                  </button>
                  <button
                    data-testid={`report-resolve-${report.report_id}`}
                    type="button"
                    disabled={busyId === report.report_id}
                    onClick={(e) => { e.stopPropagation(); onStatus(report.report_id, "resolved"); }}
                    className="td-btn-primary !py-1.5 !px-3 !text-xs"
                  >
                    <CheckCircle2 size={13} /> Mark resolved
                  </button>
                  <button
                    data-testid={`report-dismiss-${report.report_id}`}
                    type="button"
                    disabled={busyId === report.report_id}
                    onClick={(e) => { e.stopPropagation(); onStatus(report.report_id, "dismissed"); }}
                    className="td-btn-secondary !py-1.5 !px-3 !text-xs"
                  >
                    <XCircle size={13} /> Dismiss
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminReports() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState("active");
  const [expanded, setExpanded] = useState(null);
  const [confirmHide, setConfirmHide] = useState(null); // {trail_id, name, currently_hidden}

  const reportsQ = useQuery({
    queryKey: ["admin-trail-reports", status],
    enabled: !!user && !!user.is_admin,
    queryFn: async () => (await api.get("/admin/trail-reports", { params: { status } })).data,
  });

  const statusMut = useMutation({
    mutationFn: async ({ report_id, status }) =>
      (await api.patch(`/admin/trail-reports/${report_id}`, { status })).data,
    onSuccess: (_d, v) => {
      toast.success(v.status === "resolved" ? "Marked resolved" : "Dismissed");
      qc.invalidateQueries({ queryKey: ["admin-trail-reports"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't update"),
  });

  const editMut = useMutation({
    mutationFn: async ({ report_id, issue_type, sub_category, admin_notes }) => {
      const payload = { issue_type, admin_notes };
      // empty-string sub_category clears the field on backend
      payload.sub_category = sub_category || "";
      return (await api.patch(`/admin/trail-reports/${report_id}`, payload)).data;
    },
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["admin-trail-reports"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't save"),
  });

  const hideMut = useMutation({
    mutationFn: async ({ trail_id, hidden }) =>
      (await api.post(`/admin/trails/${trail_id}/hidden`, { hidden })).data,
    onSuccess: (data) => {
      toast.success(data.hidden ? `Trail "${data.name || data.trail_id}" hidden` : `Trail "${data.name || data.trail_id}" restored`);
      setConfirmHide(null);
      qc.invalidateQueries({ queryKey: ["admin-trail-reports"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't hide trail"),
  });

  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const data = reportsQ.data;
  const items = data?.items || [];
  const counts = data?.counts || { pending: 0, resolved: 0, dismissed: 0, all: 0 };
  const busyId = statusMut.isPending ? statusMut.variables?.report_id : null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-reports-page">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="td-section-label">Admin</div>
          <h1 className="font-heading text-4xl font-semibold text-brand-text mt-1 flex items-center gap-3">
            <Flag size={28} className="text-brand-primary" />
            Reported issues
          </h1>
          <p className="text-brand-subtle text-sm mt-1">
            Walker-submitted trail problems with photo evidence. Click a row to expand and act.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/telemetry" data-testid="reports-to-telemetry" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Activity size={14} /> Telemetry
          </Link>
          <Link to="/admin/users" data-testid="reports-to-users" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Users size={14} /> Users
          </Link>
          <button
            data-testid="reports-refresh"
            onClick={() => reportsQ.refetch()}
            className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="reports-summary">
        <div className="td-card p-4">
          <div className="td-section-label">Active</div>
          <div className="font-heading text-3xl text-brand-accent mt-1">{(counts.active ?? counts.pending ?? 0).toLocaleString("en-GB")}</div>
        </div>
        <div className="td-card p-4">
          <div className="td-section-label">Resolved</div>
          <div className="font-heading text-3xl text-brand-primary mt-1">{counts.resolved.toLocaleString("en-GB")}</div>
        </div>
        <div className="td-card p-4">
          <div className="td-section-label">Dismissed</div>
          <div className="font-heading text-3xl text-brand-subtle mt-1">{counts.dismissed.toLocaleString("en-GB")}</div>
        </div>
        <div className="td-card p-4">
          <div className="td-section-label">Total</div>
          <div className="font-heading text-3xl text-brand-text mt-1">{counts.all.toLocaleString("en-GB")}</div>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex gap-2 flex-wrap mb-3" data-testid="reports-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            data-testid={`status-tab-${t.value}`}
            onClick={() => { setStatus(t.value); setExpanded(null); }}
            className={
              "td-chip cursor-pointer " +
              (status === t.value ? "td-chip-active" : "")
            }
          >
            {t.label} <span className="opacity-70">· {counts[t.value] ?? 0}</span>
          </button>
        ))}
      </div>

      {reportsQ.isLoading && (
        <div className="td-card p-6 flex items-center gap-2 text-brand-subtle text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading reports…
        </div>
      )}

      {data && (
        <div className="td-card p-0 overflow-hidden" data-testid="reports-table">
          <div className="px-5 sm:px-6 pt-5 pb-3 flex items-center justify-between">
            <h2 className="font-heading text-lg font-medium">
              {STATUS_TABS.find((t) => t.value === status)?.label} reports
            </h2>
            <span className="text-xs text-brand-subtle">{items.length} shown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-sand text-brand-subtle uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-4 sm:px-5 py-2">Photo</th>
                  <th className="text-left px-3 py-2">Issue · Trail</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Description</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">Submitted</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-4 sm:px-5 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <ReportRow
                    key={r.report_id}
                    report={r}
                    expanded={expanded === r.report_id}
                    onToggle={() => setExpanded(expanded === r.report_id ? null : r.report_id)}
                    onStatus={(report_id, newStatus) => statusMut.mutate({ report_id, status: newStatus })}
                    onHideTrail={(report) => setConfirmHide({ trail_id: report.trail_id, name: report.trail_name })}
                    onSaveEdit={(payload) => editMut.mutate(payload)}
                    busyId={busyId}
                  />
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-6 text-center text-brand-subtle">No reports in this queue.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-brand-subtle mt-6">
        <Link to="/discover" className="underline" data-testid="reports-back">← Back to app</Link>
      </p>

      {/* Hide-trail confirmation modal */}
      {confirmHide && (
        <div
          className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4"
          onClick={() => setConfirmHide(null)}
          data-testid="hide-trail-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-brand-surface rounded-3xl w-full max-w-sm p-6 animate-fade-up">
            <h3 className="font-heading text-xl text-brand-text flex items-center gap-2">
              <AlertTriangle size={18} className="text-brand-error" />
              Close this trail?
            </h3>
            <p className="text-sm text-brand-subtle mt-2">
              <span className="text-brand-text font-medium break-words">{confirmHide.name || confirmHide.trail_id}</span> will be hidden from
              Discover and the map. You can restore it later from the trail&apos;s admin actions.
            </p>
            <div className="flex gap-2 mt-5">
              <button data-testid="hide-trail-cancel" onClick={() => setConfirmHide(null)} className="td-btn-secondary flex-1 !py-2.5">
                Cancel
              </button>
              <button
                data-testid="hide-trail-confirm"
                onClick={() => hideMut.mutate({ trail_id: confirmHide.trail_id, hidden: true })}
                disabled={hideMut.isPending}
                className="td-btn-primary flex-1 !py-2.5 !bg-brand-error"
              >
                {hideMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <EyeOff size={14} />}
                Yes, close trail
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
